import React, { useState, createContext, useContext } from 'react';
import { Schedule, DEFAULT_SCHEDULE } from './ScheduleModal';
export type Menu = {
  id: string;
  name: string;
  description: string;
  itemIds: string[];
  active: boolean;
  schedule: Schedule;
  color: string; // tailwind color name
  icon: string; // canonical key (sunrise/coffee/utensils/moon)
};
export type BranchItemOverride = {
  available: boolean;
  price?: number;
};
type Ctx = {
  menus: Menu[];
  addMenu: (partial?: Partial<Menu>) => string;
  updateMenu: (id: string, patch: Partial<Menu>) => void;
  removeMenu: (id: string) => void;
  toggleItem: (menuId: string, itemId: string) => void;
  branchOverrides: Record<string, Record<string, BranchItemOverride>>;
  setBranchItemAvailability: (branchId: string, itemId: string, available: boolean) => void;
  setBranchItemPrice: (branchId: string, itemId: string, price: number | undefined) => void;
};
const MenusContext = createContext<Ctx | null>(null);
const breakfastSchedule: Schedule = {
  alwaysAvailable: false,
  days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
  startTime: '07:00',
  endTime: '11:00'
};
const mainSchedule: Schedule = {
  alwaysAvailable: false,
  days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
  startTime: '11:00',
  endTime: '22:00'
};
const lateSchedule: Schedule = {
  alwaysAvailable: false,
  days: ['thu', 'fri', 'sat'],
  startTime: '22:00',
  endTime: '02:00'
};
const brunchSchedule: Schedule = {
  alwaysAvailable: false,
  days: ['sat', 'sun'],
  startTime: '10:00',
  endTime: '15:00'
};
const seed: Menu[] = [{
  id: 'm1',
  name: 'Main menu',
  description: 'Default all-day menu available across all branches.',
  itemIds: ['i_burger', 'i_pasta', 'i_salmon', 'i_avocado', 'i_caesar', 'i_latte', 'i_water'],
  active: true,
  schedule: mainSchedule,
  color: 'orange',
  icon: 'utensils'
}, {
  id: 'm2',
  name: 'Breakfast menu',
  description: 'Morning items only — eggs, toast, coffee.',
  itemIds: ['i_american', 'i_avocado', 'i_eggs', 'i_macchiato', 'i_latte', 'i_juice'],
  active: true,
  schedule: breakfastSchedule,
  color: 'amber',
  icon: 'sunrise'
}, {
  id: 'm3',
  name: 'Late night menu',
  description: 'After-hours menu with simpler kitchen prep.',
  itemIds: ['i_burger', 'i_pasta', 'i_salmon'],
  active: true,
  schedule: lateSchedule,
  color: 'indigo',
  icon: 'moon'
}, {
  id: 'm4',
  name: 'Weekend brunch',
  description: 'Saturday & Sunday late-morning specials.',
  itemIds: ['i_avocado', 'i_eggs', 'i_caesar', 'i_latte'],
  active: true,
  schedule: brunchSchedule,
  color: 'pink',
  icon: 'coffee'
}];
const palette = ['orange', 'amber', 'indigo', 'pink', 'emerald', 'blue', 'purple', 'rose'];
export function MenusProvider({
  children
}: {
  children: ReactNode;
}) {
  const [menus, setMenus] = useState<Menu[]>(seed);
  const [branchOverrides, setBranchOverrides] = useState<Record<string, Record<string, BranchItemOverride>>>({});
  const setBranchItemAvailability = (branchId: string, itemId: string, available: boolean) => {
    setBranchOverrides(prev => ({
      ...prev,
      [branchId]: {
        ...(prev[branchId] || {}),
        [itemId]: {
          ...(prev[branchId]?.[itemId] || {}),
          available
        }
      }
    }));
  };
  const setBranchItemPrice = (branchId: string, itemId: string, price: number | undefined) => {
    setBranchOverrides(prev => ({
      ...prev,
      [branchId]: {
        ...(prev[branchId] || {}),
        [itemId]: {
          ...(prev[branchId]?.[itemId] || {
            available: true
          }),
          price
        }
      }
    }));
  };
  const addMenu = (partial?: Partial<Menu>) => {
    const id = `mn_${Date.now().toString(36)}`;
    const newMenu: Menu = {
      id,
      name: 'New menu',
      description: '',
      itemIds: [],
      active: true,
      schedule: DEFAULT_SCHEDULE,
      color: palette[Math.floor(Math.random() * palette.length)],
      icon: 'utensils',
      ...partial
    };
    setMenus(prev => [...prev, newMenu]);
    return id;
  };
  const updateMenu = (id: string, patch: Partial<Menu>) => {
    setMenus(prev => prev.map(m => m.id === id ? {
      ...m,
      ...patch
    } : m));
  };
  const removeMenu = (id: string) => {
    setMenus(prev => prev.filter(m => m.id !== id));
  };
  const toggleItem = (menuId: string, itemId: string) => {
    setMenus(prev => prev.map(m => {
      if (m.id !== menuId) return m;
      const has = m.itemIds.includes(itemId);
      return {
        ...m,
        itemIds: has ? m.itemIds.filter(id => id !== itemId) : [...m.itemIds, itemId]
      };
    }));
  };
  return <MenusContext.Provider value={{
    menus,
    addMenu,
    updateMenu,
    removeMenu,
    toggleItem,
    branchOverrides,
    setBranchItemAvailability,
    setBranchItemPrice
  }} data-id="element-2399">
      {children}
    </MenusContext.Provider>;
}
export function useMenus() {
  const ctx = useContext(MenusContext);
  if (!ctx) throw new Error('useMenus must be used within MenusProvider');
  return ctx;
}
export function useBranchMenu(branchId: string | undefined) {
  const {
    branchOverrides
  } = useMenus();
  if (!branchId) return catalogItems;
  const overrides = branchOverrides[branchId] || {};
  return catalogItems.map(item => {
    const o = overrides[item.id];
    return {
      ...item,
      available: o?.available ?? true,
      price: o?.price ?? item.price
    };
  }).filter(item => item.available);
}
// Shared mock item catalog — until items live in a proper context,
// menus reference these ids for assignment.
export type CatalogItem = {
  id: string;
  name: string;
  category: string;
  stationId: string;
  price: number;
  description?: string;
  image?: string;
  modifierIds?: string[];
};
export const catalogItems: CatalogItem[] = [{
  id: 'i_american',
  name: 'American Breakfast',
  category: 'Eggs & Omelets',
  stationId: 'food',
  price: 15,
  description: 'Two eggs any style, served with beef sausage, home fries, a mini pancake, and toast bread.',
  image: 'https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?auto=format&fit=crop&w=600&q=80',
  modifierIds: ['mod_bacon', 'mod_eggs', 'mod_avocado', 'mod_hotsauce', 'mod_ketchup']
}, {
  id: 'i_eggs',
  name: 'Eggs Benedict',
  category: 'Eggs & Omelets',
  stationId: 'food',
  price: 14,
  description: 'Poached eggs and Canadian bacon on a toasted English muffin, draped in house hollandaise.',
  image: 'https://images.unsplash.com/photo-1608039829572-78524f79c4c7?auto=format&fit=crop&w=600&q=80',
  modifierIds: ['mod_poached', 'mod_avocado', 'mod_hotsauce']
}, {
  id: 'i_avocado',
  name: 'Avocado & Salmon Toast',
  category: 'Savory Plates',
  stationId: 'food',
  price: 16,
  description: 'Toasted bread topped with creamy avocado and grilled salmon, served with a green salad and roasted potatoes.',
  image: 'https://images.unsplash.com/photo-1525351484163-7529414344d8?auto=format&fit=crop&w=600&q=80',
  modifierIds: ['mod_salmon', 'mod_feta', 'mod_poached']
}, {
  id: 'i_salmon',
  name: 'Salmon Wrap',
  category: 'Salads',
  stationId: 'cold',
  price: 18,
  description: 'A warm tortilla filled with grilled salmon, sliced boiled egg, fresh vegetables, and melted cheese — a hearty, energizing start.',
  image: 'https://images.unsplash.com/photo-1626700051175-6818013e1d4f?auto=format&fit=crop&w=600&q=80',
  modifierIds: ['mod_feta', 'mod_salmon', 'mod_hotsauce']
}, {
  id: 'i_caesar',
  name: 'Caesar Salad',
  category: 'Salads',
  stationId: 'cold',
  price: 12,
  description: 'Crisp romaine tossed in classic Caesar dressing with shaved parmesan and golden croutons.',
  image: 'https://images.unsplash.com/photo-1550304943-4f24f54ddde9?auto=format&fit=crop&w=600&q=80',
  modifierIds: ['mod_feta', 'mod_salmon', 'mod_avocado']
}, {
  id: 'i_burger',
  name: 'Beef Burger Deluxe',
  category: 'Burgers and Sandwiches',
  stationId: 'food',
  price: 24,
  description: 'Juicy beef patty with melted cheddar, lettuce, tomato, and house sauce on a toasted brioche bun, with fries.',
  image: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=600&q=80',
  modifierIds: ['mod_cheese', 'mod_bacon', 'mod_avocado', 'mod_fries', 'mod_sweetfries']
}, {
  id: 'i_pasta',
  name: 'Pasta Carbonara',
  category: 'Spaghetti',
  stationId: 'food',
  price: 19,
  description: 'Spaghetti in a silky egg and parmesan sauce with crispy pancetta and cracked black pepper.',
  image: 'https://images.unsplash.com/photo-1612874742237-6526221588e3?auto=format&fit=crop&w=600&q=80',
  modifierIds: ['mod_bacon', 'mod_feta', 'mod_salad']
}, {
  id: 'i_macchiato',
  name: 'Macchiato',
  category: 'Drinks',
  stationId: 'bar',
  price: 4,
  description: 'A bold double espresso marked with a dollop of steamed milk.',
  image: 'https://images.unsplash.com/photo-1485808191679-5f86510681a2?auto=format&fit=crop&w=600&q=80'
}, {
  id: 'i_latte',
  name: 'Iced Latte',
  category: 'Drinks',
  stationId: 'bar',
  price: 5,
  description: 'Chilled espresso and cold milk over ice — smooth, refreshing, and lightly sweet.',
  image: 'https://images.unsplash.com/photo-1461023058943-07fcbe16d735?auto=format&fit=crop&w=600&q=80'
}, {
  id: 'i_juice',
  name: 'Fresh Juice',
  category: 'Drinks',
  stationId: 'bar',
  price: 6,
  description: 'Freshly pressed seasonal fruit, served cold. Ask for today’s selection.',
  image: 'https://images.unsplash.com/photo-1622597467836-f3285f2131b8?auto=format&fit=crop&w=600&q=80'
}, {
  id: 'i_water',
  name: 'Sparkling Water',
  category: 'Drinks',
  stationId: 'bar',
  price: 3,
  description: 'Chilled sparkling mineral water with a wedge of lime.',
  image: 'https://images.unsplash.com/photo-1606168094336-48f8b0c2d4f4?auto=format&fit=crop&w=600&q=80'
}, {
  id: 'i_tiramisu',
  name: 'Tiramisu',
  category: 'Desserts',
  stationId: 'dessert',
  price: 7,
  description: 'Espresso-soaked ladyfingers layered with mascarpone cream and a dusting of cocoa.',
  image: 'https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?auto=format&fit=crop&w=600&q=80'
}];
export const menuIconKeys = ['utensils', 'sunrise', 'coffee', 'moon', 'pizza', 'cake'];